import type { SVGProps } from 'react';

/**
 * Navigation and status icons.
 *
 * Inline SVG rather than an icon package: the console must load nothing from the
 * internet at runtime (§7), and a handful of 24px strokes is smaller than any
 * dependency. Every icon inherits `currentColor` and is marked decorative — the
 * label beside it carries the meaning, so a missing glyph never hides a state.
 */

export type IconName =
  | 'overview'
  | 'schools'
  | 'devices'
  | 'audio'
  | 'schedules'
  | 'calendar'
  | 'deployments'
  | 'playback'
  | 'users'
  | 'audit'
  | 'settings'
  | 'emergency'
  | 'logout'
  | 'menu'
  | 'close';

const PATHS: Record<IconName, JSX.Element> = {
  // Dashboard: four panes.
  overview: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </>
  ),
  // Schools: a school building with a flag.
  schools: (
    <>
      <path d="M3 21h18" />
      <path d="M5 21V9.5l7-4.5 7 4.5V21" />
      <path d="M10 21v-5h4v5" />
      <path d="M12 5V2.5" />
    </>
  ),
  // Devices: a wall unit with a speaker grille.
  devices: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2.5" />
      <circle cx="12" cy="10" r="3" />
      <path d="M9 16.5h6" />
    </>
  ),
  // Audio library: a stack with a note.
  audio: (
    <>
      <path d="M9 18V6l10-2v12" />
      <circle cx="6.5" cy="18" r="2.5" />
      <circle cx="16.5" cy="16" r="2.5" />
    </>
  ),
  // Schedules: a clock.
  schedules: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5V12l3.5 2" />
    </>
  ),
  // Calendar exceptions: a month grid with a marked day.
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2.5" />
      <path d="M3 10h18M8 5V2.5M16 5V2.5" />
      <path d="M11 14.5h2v2h-2z" fill="currentColor" stroke="none" />
    </>
  ),
  // Deployments: an upload arrow leaving a tray.
  deployments: (
    <>
      <path d="M12 16V4" />
      <path d="M7.5 8.5 12 4l4.5 4.5" />
      <path d="M4 15v3.5A2.5 2.5 0 0 0 6.5 21h11A2.5 2.5 0 0 0 20 18.5V15" />
    </>
  ),
  // Manual playback: play inside a circle.
  playback: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 8.5l6 3.5-6 3.5z" fill="currentColor" stroke="none" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20.5c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" />
      <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" />
      <path d="M17.5 14.4c2.4.7 4 2.9 4 5.4" />
    </>
  ),
  // Audit log: lines of record.
  audit: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2.5" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v3M12 18.5v3M4.2 7l2.6 1.5M17.2 15.5l2.6 1.5M4.2 17l2.6-1.5M17.2 8.5l2.6-1.5" />
    </>
  ),
  // Emergency: a warning triangle.
  emergency: (
    <>
      <path d="M12 3.5 21.5 20H2.5z" />
      <path d="M12 9v5" />
      <path d="M12 17h.01" strokeWidth="2.5" />
    </>
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  logout: (
    <>
      <path d="M15 4.5H18A2.5 2.5 0 0 1 20.5 7v10a2.5 2.5 0 0 1-2.5 2.5h-3" />
      <path d="M11 8l-4 4 4 4" />
      <path d="M7 12h9" />
    </>
  ),
};

export function Icon({
  name,
  size = 18,
  ...rest
}: { name: IconName; size?: number } & SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
